// Folds the earlier email a reply quotes behind a "•••" toggle, like other mail apps do. The
// toggle is a plain <details>, so it works inside the message frame, where no script runs.

import { isAttribution, quoteLevels, splitQuote } from "../shared/quotes";
import { escapeHtml, textToHtml } from "./format";

/** Where mail apps put the email being answered. Forwards are left alone: they are the content. */
const QUOTE_SELECTOR = [
  ".gmail_quote_container",
  ".gmail_quote",
  ".cm-quote-intro",
  'blockquote[type="cite"]',
  "#appendonsend",
  "#divRplyFwdMsg",
  "#mail-editor-reference-message-container",
  ".yahoo_quoted",
  ".protonmail_quote",
  ".moz-cite-prefix",
  "div.OutlookMessageHeader",
  'div[style*="border-top:solid #E1E1E1"]',
  'div[style*="border-top: solid #E1E1E1"]',
  'div[style*="border-top:solid #B5C4DF"]',
].join(",");

const TOGGLE =
  '<summary title="Show quoted text" aria-label="Show quoted text"><span>•••</span></summary>';

/** Styles for the toggle and the quoted text it opens, for the message frame. */
export const QUOTE_STYLE = `
  details.cm-quoted{margin:.75em 0 0;}
  details.cm-quoted>summary{list-style:none;display:inline-flex;align-items:center;min-width:40px;height:24px;padding:0;box-sizing:border-box;border-radius:11px;cursor:pointer;user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;}
  details.cm-quoted>summary::-webkit-details-marker{display:none;}
  details.cm-quoted>summary span{display:block;padding:0 6px;border-radius:7px;background:rgba(128,128,128,.2);color:inherit;font:700 11px/14px sans-serif;letter-spacing:1px;opacity:.75;}
  details.cm-quoted>summary:hover span,details.cm-quoted>summary:focus-visible span{opacity:1;background:rgba(128,128,128,.32);}
  details.cm-quoted[open]>summary{margin-bottom:.5em;}
  .cm-quoted .cm-quote-intro{opacity:.75;margin-bottom:.25em;}
  .cm-quoted blockquote{margin:0;padding-left:1em;border-left:3px solid rgba(128,128,128,.4);}
`;

const visible = (node: Node): boolean => {
  if (node.nodeType === Node.TEXT_NODE)
    return (node.textContent ?? "").replace(/ /g, " ").trim() !== "";
  if (!(node instanceof Element)) return false;
  if (/^(img|video|svg|table|iframe)$/i.test(node.tagName)) return true;
  if (/^(style|script|head|title|meta)$/i.test(node.tagName)) return false;
  return (
    (node.textContent ?? "").replace(/ /g, " ").trim() !== "" ||
    node.querySelector("img, video, svg, table") !== null
  );
};

/** Whether anything shows outside `nodes` in the body. */
const hasOtherContent = (body: HTMLElement, nodes: readonly Node[]) => {
  const walker = body.ownerDocument.createTreeWalker(
    body,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
  );
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (nodes.some((inside) => inside === node || inside.contains(node))) continue;
    if (
      node.nodeType === Node.TEXT_NODE
        ? visible(node)
        : /^(img|video|svg)$/i.test((node as Element).tagName)
    )
      return true;
  }
  return false;
};

/** The node's lines, for spotting "> " quoting in HTML that marks it up as plain text. */
const lines = (node: Node) =>
  (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "BR"
    ? ""
    : (node.textContent ?? "")
  )
    .replace(/ /g, " ")
    .split("\n")
    .filter((line) => line.trim());

/** Quote containers the mail app marked up, with the "On … wrote:" line or rule just above. */
const markedQuote = (body: HTMLElement): Node[] | null => {
  const found = body.querySelector(QUOTE_SELECTOR);
  if (!found || found === body) return null;
  const outlook = found.id === "appendonsend" || found.id === "divRplyFwdMsg";
  let nodes = around(found, outlook);
  // Apple Mail wraps the intro line in a quote of its own, inside a div, beside the real quote:
  // while the quote is all its parent shows, take the parent and look around it instead.
  for (let parent = found.parentElement; parent && parent !== body; parent = parent.parentElement) {
    const shown = [...parent.childNodes].filter(visible);
    if (!shown.every((node) => nodes.includes(node))) break;
    nodes = around(parent, outlook);
  }
  // An answer written between quoted parts needs the quotes for context.
  const last = nodes[nodes.length - 1];
  if (!last) return null;
  const later = [...body.querySelectorAll(`${QUOTE_SELECTOR}, blockquote`)].filter(
    (element) =>
      last.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING &&
      !last.contains(element),
  );
  return later.length > 0 ? null : nodes;
};

/** The quote with the intro line or rule just above it and any quotes right after it. */
const around = (quote: Node, outlook: boolean): Node[] => {
  const nodes: Node[] = [quote];
  // Apple Mail, Thunderbird and Outlook put the intro line or a rule in a sibling above.
  for (let previous = quote.previousSibling; previous; previous = previous.previousSibling) {
    const text = (previous.textContent ?? "").trim();
    const tag = previous instanceof Element ? previous.tagName : "";
    if (
      !visible(previous) ||
      tag === "HR" ||
      (text.length < 400 && isAttribution(text.replace(/\s+/g, " ")))
    )
      nodes.unshift(previous);
    else break;
  }
  for (let next = quote.nextSibling; next; next = next.nextSibling) {
    // Outlook web puts the original email in plain siblings after its header.
    if (
      outlook ||
      !visible(next) ||
      (next instanceof Element && next.matches(`${QUOTE_SELECTOR}, blockquote`))
    )
      nodes.push(next);
    else break;
  }
  const kept = (node: Node) => visible(node) || (node instanceof Element && node.tagName === "HR");
  while (nodes.length > 0 && !kept(nodes[0] as Node)) nodes.shift();
  while (nodes.length > 0 && !visible(nodes[nodes.length - 1] as Node)) nodes.pop();
  return nodes;
};

/** HTML that quotes with "> " lines, the way plain-text mail does. */
const textQuote = (body: HTMLElement): Node[] | null => {
  let container: Element = body;
  // Mail apps wrap everything in a div or two.
  for (;;) {
    const children = [...container.childNodes].filter(visible);
    const only = children[0];
    if (children.length === 1 && only instanceof Element && only.childNodes.length > 1)
      container = only;
    else break;
  }
  const children = [...container.childNodes];
  const quoted = (node: Node) =>
    lines(node).every((line) => line.trimStart().startsWith(">") || isAttribution(line));
  for (let start = 0; start < children.length; start++) {
    const first = lines(children[start] as Node)[0];
    if (!first || !(first.trimStart().startsWith(">") || isAttribution(first))) continue;
    let end = start + 1;
    while (end < children.length && quoted(children[end] as Node)) end++;
    const nodes = children.slice(start, end);
    while (nodes.length > 0 && !visible(nodes[nodes.length - 1] as Node)) nodes.pop();
    // Only a bare intro line, or more quoting further down: leave it.
    if (!nodes.some((node) => lines(node).some((line) => line.trimStart().startsWith(">"))))
      continue;
    if (
      children
        .slice(end)
        .some((node) => lines(node).some((line) => line.trimStart().startsWith(">")))
    )
      return null;
    return nodes;
  }
  return null;
};

/** Moves the quoted part of an HTML email into a closed <details>. Returns the new body HTML. */
export const foldHtmlQuote = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;
  const nodes = markedQuote(body) ?? textQuote(body);
  const first = nodes?.[0];
  if (!nodes || !first?.parentNode || !hasOtherContent(body, nodes)) return html;
  const details = doc.createElement("details");
  details.className = "cm-quoted";
  details.innerHTML = TOGGLE;
  first.parentNode.insertBefore(details, first);
  for (const node of nodes) details.append(node);
  // With <head> and <body>, so the sender's styles and body colors still apply in the frame.
  return doc.documentElement.innerHTML;
};

/** Theme the reply separately from the history folded by the reader. */
export const withoutHtmlQuote = (html: string): string => {
  // Ordinary messages need no DOM walk. Use the same markers as the quote folder.
  if (
    !/(?:<blockquote\b|gmail_quote|yahoo_quoted|protonmail_quote|moz-cite-prefix|appendonsend|divRplyFwdMsg|mail-editor-reference-message-container|OutlookMessageHeader|cm-quoted|cm-quote-intro|E1E1E1|B5C4DF|&gt;|&#62;|&#x3e;)/i.test(
      html,
    )
  )
    return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const folded = [...doc.body.querySelectorAll("details.cm-quoted")];
  const nodes = folded.length ? folded : (markedQuote(doc.body) ?? textQuote(doc.body));
  // A forward/quote without a reply is still the message itself.
  if (!nodes?.length || !hasOtherContent(doc.body, nodes)) return html;
  for (const node of nodes) node.parentNode?.removeChild(node);
  return doc.documentElement.innerHTML;
};

/** Quoted plain text as nested quote boxes, one per "> " level. */
const quotedTextHtml = (quoted: string) => {
  let html = "";
  let level = 0;
  for (const [depth, line] of quoteLevels(quoted)) {
    while (level < depth) {
      html += "<blockquote>";
      level++;
    }
    while (level > depth) {
      html += "</blockquote>";
      level--;
    }
    html += isAttribution(line)
      ? `<div class="cm-quote-intro">${escapeHtml(line)}</div>`
      : `${textToHtml(line)}<br>`;
  }
  return html + "</blockquote>".repeat(level);
};

/** A plain-text email as HTML, with the quoted email folded behind the toggle. */
export const foldTextQuote = (text: string): string => {
  const split = splitQuote(text);
  const plain = (value: string) => `<div style="white-space:pre-wrap">${textToHtml(value)}</div>`;
  if (!split.quoted) return plain(text);
  return [
    split.before.trim() && plain(split.before.replace(/\s+$/, "")),
    `<details class="cm-quoted">${TOGGLE}<div style="white-space:pre-wrap">${quotedTextHtml(split.quoted)}</div></details>`,
    split.after.trim() && plain(split.after.replace(/^\s*\n/, "")),
  ]
    .filter(Boolean)
    .join("");
};
